import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  PlannedResourceRow,
  useIcs204aVersions,
  type Ics204aVersionPayload,
} from 'components/IAP/hooks/assignmentListHooks';
import { useCurrentPeriod } from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { ICS_204A_FORM_NAME } from 'components/IAP/hooks/workAssignmentListAttachmentHooks';
import type { PlannedResourceDraft } from './assignmentCardFormTypes';
import {
  ICS204_HINT,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
} from './assignmentCardStyles';
import { Ics204CardSection } from './ics204BoxLabels';
import {
  ICS204A_BOX_LABELS,
  ICS204A_SPECIAL_INSTRUCTIONS_HINT,
} from './ics204aBoxLabels';
import {
  renderIcs204aSectionEditor,
  type Ics204aDisplayFields,
} from './Ics204aSectionEditors';
import type { Ics204aEditingSection } from './ics204aSections';
import Ics204aVersioningPanel from './Ics204aVersioningPanel';
import type { WalaVersionRow } from './Ics204VersionsDialog';
import {
  isVersionLockedBySignatures,
  parseWalaSignatures,
} from './ics204aSignatureSlots';
import { useIcs204HeaderContext } from './useIcs204HeaderContext';
import { OperationalPeriodScheduleTrigger } from '../OperationalPeriodScheduleControl';

const DIALOG_SURFACE =
  'dark tw:max-w-[75vw] tw:sm:max-w-[75vw] tw:border-[var(--border-primary)] tw:bg-black tw:text-foreground';

const SECTION_CLASS = 'tw:mt-0 tw:h-full';

const INCIDENT_NAME_TOOLTIP =
  'This section is read-only. It is automatically filled from the incident name.';
const INCIDENT_LOCATION_TOOLTIP =
  'This section is read-only. It is automatically filled from the incident location.';
const OPERATIONAL_PERIOD_TOOLTIP =
  'Click to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';
const STRIKE_TEAM_RESOURCE_TOOLTIP =
  'This section is read-only. It is automatically filled from the planned resource this 204A is attached to on the Assignment List, and cannot be edited here.';

type ReadOnlySectionInfoProps = {
  ariaLabel: string;
  testId: string;
  tooltip: string;
};

function ReadOnlySectionInfo({
  ariaLabel,
  testId,
  tooltip,
}: ReadOnlySectionInfoProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              role="button"
              tabIndex={0}
              aria-label={ariaLabel}
              className="tw:inline-flex tw:shrink-0 tw:text-[var(--text-weak)]"
              data-testid={testId}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Info className="tw:h-3.5 tw:w-3.5" />
            </span>
          }
        />
        <TooltipContent side="bottom" className="tw:max-w-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function displayFromResource(
  resource: PlannedResourceRow
): Ics204aDisplayFields {
  return {
    resourceBranch: resource.resourceBranch || '',
    resourceDivision: resource.resourceDivision || '',
    resourceGroup: resource.resourceGroup || '',
    resourceLeader: resource.leader || '',
    resourceAssignmentLocation: resource.resourceAssignmentLocation || '',
    resourceSpecialInstructions: resource.resourceSpecialInstructions || '',
    resourceSafetyPlanLocation: resource.resourceSafetyPlanLocation || '',
  };
}

function displayFromPayload(
  resource: PlannedResourceRow,
  payload: Ics204aVersionPayload | null | undefined
): Ics204aDisplayFields {
  if (!payload) return displayFromResource(resource);
  return {
    resourceBranch: payload.resourceBranch || '',
    resourceDivision: payload.resourceDivision || '',
    resourceGroup: payload.resourceGroup || '',
    resourceLeader: payload.leader || '',
    resourceAssignmentLocation: payload.resourceAssignmentLocation || '',
    resourceSpecialInstructions: payload.resourceSpecialInstructions || '',
    resourceSafetyPlanLocation: payload.resourceSafetyPlanLocation || '',
  };
}

function displayText(value: string | undefined | null): string {
  const trimmed = value?.trim() ?? '';
  return trimmed ? trimmed : NULL_VALUE;
}

/** Ignore select/menu/add-new portals; do not ignore the 204A dialog itself. */
const isIgnorablePortaledUi = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return !!target.closest(
    [
      '[data-slot="select-content"]',
      '[data-slot="dropdown-menu-content"]',
      '[data-slot="popover-content"]',
      '[data-slot="tooltip-content"]',
      '[data-slot="alert-dialog-content"]',
      '[role="listbox"]',
      '[data-radix-popper-content-wrapper]',
      '[role="alertdialog"]',
    ].join(', ')
  );
};

type AssignmentCardResourceAttachmentDialogProps = {
  onClose: () => void;
  data: PlannedResourceRow;
  /**
   * When supplied, the 204A is handed to the caller instead of being written to
   * the server. The ICS-204 edit form uses this so its own save is the single
   * writer -- `persist` resends every 204A column, so a direct write here would
   * be reverted by it.
   */
  onCommit?: (fields: Partial<PlannedResourceDraft>) => void;
  /** Future OP number for Boxes 1–3; defaults to current + 1. */
  periodNumber?: number;
};

const AssignmentCardResourceAttachmentDialog = ({
  onClose,
  data: resourceAttachmentData,
  onCommit,
  periodNumber: periodNumberProp,
}: AssignmentCardResourceAttachmentDialogProps) => {
  const { periodNumber: currentPeriodNumber } = useCurrentPeriod();
  const periodNumber = periodNumberProp ?? currentPeriodNumber + 1;
  const { incidentName, opFrom, opTo } = useIcs204HeaderContext(periodNumber);
  const { data: incidentInfo } = useIncidentInfo();
  const incidentLocation = incidentInfo?.incidentLocation?.trim() ?? '';

  const walaId = resourceAttachmentData.walaId;
  const plannedResourceKey = resourceAttachmentData.id;

  const { data: versions = [] } = useIcs204aVersions(
    walaId ?? '',
    plannedResourceKey
  );
  const tip = versions.length > 0 ? versions[versions.length - 1] : undefined;
  const tipSignatures = parseWalaSignatures(tip?.signatures);
  const contentLocked = isVersionLockedBySignatures(tipSignatures);

  const [isCreatingSignedVersion, setIsCreatingSignedVersion] = useState(false);
  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);
  const [editingSection, setEditingSection] =
    useState<Ics204aEditingSection | null>(null);
  const [displayFields, setDisplayFields] = useState<Ics204aDisplayFields>(() =>
    displayFromResource(resourceAttachmentData)
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const pendingSectionRef = useRef<Ics204aEditingSection | null>(null);

  const viewingVersion = useMemo(
    () =>
      viewingVersionId
        ? versions.find((version) => version.id === viewingVersionId)
        : undefined,
    [viewingVersionId, versions]
  );

  useEffect(() => {
    if (viewingVersion) {
      setDisplayFields(
        displayFromPayload(
          resourceAttachmentData,
          viewingVersion.payload as Ics204aVersionPayload | null | undefined
        )
      );
      setEditingSection(null);
      return;
    }
    setDisplayFields(displayFromResource(resourceAttachmentData));
  }, [resourceAttachmentData, viewingVersion]);

  const canEditSections =
    !viewingVersionId && (!contentLocked || isCreatingSignedVersion);

  const submitEditingSection = () => {
    const form = contentRef.current?.querySelector<HTMLFormElement>(
      '[data-testid^="ics204a-section-edit-"]'
    );
    form?.requestSubmit();
  };

  const activateSection = (section: Ics204aEditingSection) => {
    if (!canEditSections) return;
    if (editingSection === section) return;
    if (editingSection) {
      // Autosave the open section, then open the clicked one (one at a time).
      pendingSectionRef.current = section;
      const form = contentRef.current?.querySelector<HTMLFormElement>(
        '[data-testid^="ics204a-section-edit-"]'
      );
      if (form) {
        form.requestSubmit();
      } else {
        pendingSectionRef.current = null;
        setEditingSection(section);
      }
      return;
    }
    setEditingSection(section);
  };

  const handleSectionSaved = () => {
    const pending = pendingSectionRef.current;
    pendingSectionRef.current = null;
    setEditingSection(null);
    if (pending) {
      queueMicrotask(() => setEditingSection(pending));
    }
  };

  const handleSectionDiscard = () => {
    pendingSectionRef.current = null;
    setEditingSection(null);
  };

  useEffect(() => {
    if (!editingSection) return;

    const onPointerDown = (event: PointerEvent) => {
      if (isIgnorablePortaledUi(event.target)) return;
      const editingRoot = contentRef.current?.querySelector(
        '[data-ics204-section-editing="true"]'
      );
      if (!editingRoot) return;
      if (event.target instanceof Node && editingRoot.contains(event.target)) {
        return;
      }
      // Clicking another 204A section: let activateSection switch on click
      // (avoid double-submit race that clears editing without opening the next).
      if (
        event.target instanceof Element &&
        event.target.closest('[data-testid^="ics204a-section-"]') &&
        !editingRoot.contains(event.target)
      ) {
        return;
      }
      submitEditingSection();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [editingSection]);

  const sectionEditorProps = {
    plannedResource: {
      ...resourceAttachmentData,
      leader: displayFields.resourceLeader,
      resourceBranch: displayFields.resourceBranch,
      resourceDivision: displayFields.resourceDivision,
      resourceGroup: displayFields.resourceGroup,
      resourceAssignmentLocation: displayFields.resourceAssignmentLocation,
      resourceSpecialInstructions: displayFields.resourceSpecialInstructions,
      resourceSafetyPlanLocation: displayFields.resourceSafetyPlanLocation,
    },
    display: displayFields,
    onCommit,
    onLocalUpdate: (fields: Partial<Ics204aDisplayFields>) => {
      setDisplayFields((prev) => ({ ...prev, ...fields }));
    },
    onDone: handleSectionSaved,
    onCancel: handleSectionDiscard,
  };

  const sectionBody = (
    section: Ics204aEditingSection,
    view: ReactNode
  ): ReactNode => {
    if (editingSection === section) {
      return renderIcs204aSectionEditor(section, sectionEditorProps);
    }
    return view;
  };

  const sectionActivateProps = (section: Ics204aEditingSection) => ({
    isEditing: editingSection === section,
    onSectionActivate: canEditSections
      ? () => activateSection(section)
      : undefined,
  });

  const getSignedVersionPayload = (): Ics204aVersionPayload => ({
    leader: displayFields.resourceLeader,
    numberOfPersons: resourceAttachmentData.numberOfPersons ?? 0,
    contactInfo: resourceAttachmentData.contactInfo ?? '',
    reportingInfo: resourceAttachmentData.reportingInfo ?? '',
    has204a: true,
    documentationName: resourceAttachmentData.documentationName ?? null,
    resourceBranch: displayFields.resourceBranch,
    resourceDivision: displayFields.resourceDivision,
    resourceGroup: displayFields.resourceGroup,
    resourceStrikeTeam: resourceAttachmentData.resourceStrikeTeam,
    resourceTaskForce: resourceAttachmentData.resourceTaskForce,
    resourceAssignmentLocation: displayFields.resourceAssignmentLocation,
    resourceSpecialInstructions: displayFields.resourceSpecialInstructions,
    resourceSafetyPlanLocation: displayFields.resourceSafetyPlanLocation,
  });

  const syncLocalDraftAfterVersionWrite = () => {
    if (!onCommit) return;
    onCommit({
      has204a: true,
      leader: displayFields.resourceLeader,
      resourceBranch: displayFields.resourceBranch,
      resourceDivision: displayFields.resourceDivision,
      resourceGroup: displayFields.resourceGroup,
      resourceAssignmentLocation: displayFields.resourceAssignmentLocation,
      resourceSpecialInstructions: displayFields.resourceSpecialInstructions,
      resourceSafetyPlanLocation: displayFields.resourceSafetyPlanLocation,
    });
  };

  const { exportPlannedResourceAsForm } = useIcsFormPdfPreview();

  const handleExport = (format: 'pdf' | 'docx') => {
    if (resourceAttachmentData.walaId) {
      void exportPlannedResourceAsForm({
        walaId: resourceAttachmentData.walaId,
        plannedResourceId: resourceAttachmentData.id,
        format,
      });
    }
  };

  const previewDisabled =
    !!onCommit ||
    !resourceAttachmentData.has204a ||
    !resourceAttachmentData.walaId;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className={DIALOG_SURFACE}
        showCloseButton={false}
        data-testid="ics204a-dialog"
      >
        <div className="tw:flex tw:max-h-[85vh] tw:flex-col tw:gap-3">
          <DialogHeader>
            <div className="tw:flex tw:w-full tw:items-start tw:justify-between tw:gap-2">
              <div>
                <DialogTitle>Assignment List Attachment ICS-204A</DialogTitle>
                <p className={`${ICS204_HINT} tw:mt-1`}>
                  {resourceAttachmentData.name}
                </p>
              </div>
              <FormExportButton
                formname={ICS_204A_FORM_NAME}
                label="Preview ICS-204A"
                disabled={previewDisabled}
                onExport={handleExport}
              />
            </div>
          </DialogHeader>

          <div
            ref={contentRef}
            className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:pr-1"
          >
            <Ics204aVersioningPanel
              walaId={walaId}
              plannedResourceKey={plannedResourceKey}
              tipId={tip?.id}
              tipSignatures={tipSignatures}
              tipUpdatedAt={tip?.updatedAt}
              tipUpdatedByUserName={tip?.updatedByUserName}
              isCreatingSignedVersion={isCreatingSignedVersion}
              viewingVersionId={viewingVersionId}
              getSignedVersionPayload={getSignedVersionPayload}
              onStartCreateSignedVersion={() => {
                setEditingSection(null);
                setIsCreatingSignedVersion(true);
              }}
              onCancelCreateSignedVersion={() =>
                setIsCreatingSignedVersion(false)
              }
              onSignedVersionCreated={() => {
                setIsCreatingSignedVersion(false);
                setViewingVersionId(null);
                setEditingSection(null);
                syncLocalDraftAfterVersionWrite();
              }}
              onUnsignedVersionCreated={() => {
                setViewingVersionId(null);
                setEditingSection(null);
                syncLocalDraftAfterVersionWrite();
              }}
              onViewVersion={(version: WalaVersionRow) => {
                setIsCreatingSignedVersion(false);
                setEditingSection(null);
                setViewingVersionId(version.id);
              }}
              onViewLatest={() => setViewingVersionId(null)}
            >
              <div className="tw:space-y-3">
                <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
                  <Ics204CardSection
                    boxLabel={ICS204A_BOX_LABELS.incidentName}
                    testId="ics204a-section-incident-name"
                    className={SECTION_CLASS}
                    headerAction={
                      <ReadOnlySectionInfo
                        ariaLabel="About Incident Name"
                        testId="ics204a-incident-name-info"
                        tooltip={INCIDENT_NAME_TOOLTIP}
                      />
                    }
                  >
                    <span
                      className="tw:text-foreground"
                      data-testid="ics204a-field-incident-name"
                    >
                      {incidentName.trim() ? incidentName : NULL_VALUE}
                    </span>
                  </Ics204CardSection>
                  <Ics204CardSection
                    boxLabel={ICS204A_BOX_LABELS.incidentLocation}
                    testId="ics204a-section-incident-location"
                    className={SECTION_CLASS}
                    headerAction={
                      <ReadOnlySectionInfo
                        ariaLabel="About Incident Location"
                        testId="ics204a-incident-location-info"
                        tooltip={INCIDENT_LOCATION_TOOLTIP}
                      />
                    }
                  >
                    <span
                      className="tw:text-foreground"
                      data-testid="ics204a-field-incident-location"
                    >
                      {incidentLocation || NULL_VALUE}
                    </span>
                  </Ics204CardSection>
                  <Ics204CardSection
                    boxLabel={ICS204A_BOX_LABELS.operationalPeriod}
                    testId="ics204a-section-operational-period"
                    className={SECTION_CLASS}
                    headerAction={
                      <ReadOnlySectionInfo
                        ariaLabel="About Operational Period"
                        testId="ics204a-operational-period-info"
                        tooltip={OPERATIONAL_PERIOD_TOOLTIP}
                      />
                    }
                  >
                    <OperationalPeriodScheduleTrigger testId="ics204a-field-operational-period">
                      {opFrom && opTo ? `${opFrom} – ${opTo}` : NULL_VALUE}
                    </OperationalPeriodScheduleTrigger>
                  </Ics204CardSection>
                </div>

                <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
                  <Ics204CardSection
                    boxLabel={ICS204A_BOX_LABELS.branch}
                    testId="ics204a-section-branch"
                    className={SECTION_CLASS}
                    {...sectionActivateProps('branch')}
                  >
                    {sectionBody(
                      'branch',
                      <span className="tw:text-foreground">
                        {displayText(displayFields.resourceBranch)}
                      </span>
                    )}
                  </Ics204CardSection>
                  <Ics204CardSection
                    boxLabel={ICS204A_BOX_LABELS.divisionGroup}
                    testId="ics204a-section-division-group"
                    className={SECTION_CLASS}
                    {...sectionActivateProps('divisionGroup')}
                  >
                    {sectionBody(
                      'divisionGroup',
                      <div className="tw:grid tw:grid-cols-1 tw:gap-1 tw:sm:grid-cols-2">
                        <span className="tw:text-foreground">
                          {displayText(displayFields.resourceDivision)}
                        </span>
                        <span className="tw:text-foreground">
                          {displayText(displayFields.resourceGroup)}
                        </span>
                      </div>
                    )}
                  </Ics204CardSection>
                </div>

                <Ics204CardSection
                  boxLabel={ICS204A_BOX_LABELS.strikeTeamTaskForceResource}
                  testId="ics204a-section-strike-team-task-force-resource"
                  className={SECTION_CLASS}
                  headerAction={
                    <ReadOnlySectionInfo
                      ariaLabel="About Strike Team / Task Force / Resource"
                      testId="ics204a-strike-team-task-force-resource-info"
                      tooltip={STRIKE_TEAM_RESOURCE_TOOLTIP}
                    />
                  }
                >
                  <span
                    className="tw:text-foreground"
                    data-testid="ics204a-edit-resource-name"
                  >
                    {resourceAttachmentData.name?.trim()
                      ? resourceAttachmentData.name
                      : NULL_VALUE}
                  </span>
                </Ics204CardSection>

                <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
                  <Ics204CardSection
                    boxLabel={ICS204A_BOX_LABELS.leader}
                    testId="ics204a-section-leader"
                    className="tw:mt-0 tw:h-full tw:md:col-span-2"
                    {...sectionActivateProps('leader')}
                  >
                    {sectionBody(
                      'leader',
                      <span className="tw:text-foreground">
                        {displayText(displayFields.resourceLeader)}
                      </span>
                    )}
                  </Ics204CardSection>
                  <Ics204CardSection
                    boxLabel={ICS204A_BOX_LABELS.assignmentLocation}
                    testId="ics204a-section-assignment-location"
                    className={SECTION_CLASS}
                    {...sectionActivateProps('assignmentLocation')}
                  >
                    {sectionBody(
                      'assignmentLocation',
                      <span className="tw:text-foreground">
                        {displayText(displayFields.resourceAssignmentLocation)}
                      </span>
                    )}
                  </Ics204CardSection>
                </div>

                <Ics204CardSection
                  boxLabel={ICS204A_BOX_LABELS.specialInstructions}
                  testId="ics204a-section-special-instructions"
                  className="tw:mt-0"
                  {...sectionActivateProps('specialInstructions')}
                >
                  {sectionBody(
                    'specialInstructions',
                    <>
                      <p className={ICS204_HINT}>
                        {ICS204A_SPECIAL_INSTRUCTIONS_HINT}
                      </p>
                      <span
                        className={
                          displayFields.resourceSpecialInstructions.trim()
                            ? 'tw:whitespace-pre-wrap tw:text-foreground'
                            : ICS204_MUTED
                        }
                      >
                        {displayText(displayFields.resourceSpecialInstructions)}
                      </span>
                    </>
                  )}
                </Ics204CardSection>

                <Ics204CardSection
                  boxLabel={ICS204A_BOX_LABELS.siteSafetyPlan}
                  testId="ics204a-section-site-safety-plan"
                  className="tw:mt-0"
                  {...sectionActivateProps('siteSafetyPlan')}
                >
                  {sectionBody(
                    'siteSafetyPlan',
                    <span
                      className={
                        displayFields.resourceSafetyPlanLocation.trim()
                          ? 'tw:whitespace-pre-wrap tw:text-foreground'
                          : ICS204_MUTED
                      }
                    >
                      {displayText(displayFields.resourceSafetyPlanLocation)}
                    </span>
                  )}
                </Ics204CardSection>
              </div>
            </Ics204aVersioningPanel>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className={ICS204_OUTLINE_BTN}
              onClick={onClose}
            >
              Close
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentCardResourceAttachmentDialog;
